import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children, environment = 'live' }) => {
  const tokenKey = environment === 'test' ? 'rtms_token_test' : 'rtms_token';
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem(tokenKey));
  const [loading, setLoading] = useState(true);

  // Configure axios defaults — auth header + environment header
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
    axios.defaults.headers.common['X-Environment'] = environment;
  }, [token, environment]);

  // Global response interceptor: normalize FastAPI 422 `detail` (which is an
  // array of validation error objects) into a readable string so downstream
  // `toast.error(err.response.data.detail)` calls never receive an object and
  // never crash React with "Objects are not valid as a React child".
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (r) => r,
      (error) => {
        try {
          const detail = error?.response?.data?.detail;
          if (Array.isArray(detail)) {
            error.response.data.detail = detail
              .map((d) => {
                if (!d) return '';
                if (typeof d === 'string') return d;
                const loc = Array.isArray(d.loc) ? d.loc.filter((p) => p !== 'body').join('.') : '';
                return loc ? `${loc}: ${d.msg || 'invalid'}` : (d.msg || JSON.stringify(d));
              })
              .filter(Boolean)
              .join('; ') || 'Validation error';
          } else if (detail && typeof detail === 'object') {
            error.response.data.detail = detail.msg || JSON.stringify(detail);
          }
        } catch (_) { /* leave error as-is */ }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API_URL}/auth/me`);
        setUser(response.data);
      } catch (error) {
        console.error('Token verification failed:', error);
        localStorage.removeItem(tokenKey);
        setToken(null);
        setUser(null);
        delete axios.defaults.headers.common['Authorization'];
      } finally {
        setLoading(false);
      }
    };

    verifyToken();
  }, [token, tokenKey]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/auth/login`, { email, password });
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem(tokenKey, access_token);
      setToken(access_token);
      setUser(userData);
      
      return { success: true };
    } catch (error) {
      console.error('Login failed:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed. Please try again.' 
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post(`${API_URL}/auth/register`, userData);
      return { success: true, user: response.data };
    } catch (error) {
      console.error('Registration failed:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Registration failed. Please try again.' 
      };
    }
  };

  const logout = useCallback(() => {
    localStorage.removeItem(tokenKey);
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  }, [tokenKey]);

  const value = {
    user,
    token,
    loading,
    environment,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    isAdmin: user?.role === 'admin',
    isSupervisor: user?.role === 'supervisor' || user?.role === 'admin',
    isDriver: user?.role === 'driver',
    isManagement: user?.role === 'management' || user?.role === 'admin',
    isAuditor: user?.role === 'auditor',
    isMaster: user?.role === 'master',
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
